import React, { useState, useMemo, useEffect } from "react";
import { sortBy } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import {
    Avatar,
    Dialog,
    DialogContent,
    DialogTitle,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Typography
} from "@mui/material/";
import actions from "../Store/actions";
import { strings } from "../common/i18n";
import { logToSentry } from "../config/Sentry";

/**
 * User Organisation Dialog Component
 */
export default ({ onChange }) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const dispatch = useDispatch();
    const { userOrganisations } = useSelector((state) => state);

    const data = useMemo(() => {
        if (userOrganisations.data.length > 0) {
            return sortBy(userOrganisations.data, (item) => item.name);
        }
        return [];
    }, [userOrganisations]);

    useEffect(() => {
        if (
            userOrganisations.item === null &&
            userOrganisations.data.length > 1
        ) {
            setDialogOpen(true);
        }
    }, [userOrganisations]);

    const handleClick = (item: any) => async () => {
        try {
            dispatch(actions.auditsList.create.clearAudits());
            dispatch(actions.audits.create.clear());
            dispatch(actions.userOrganisations.create.itemSet(item));
            setDialogOpen(false);
            await dispatch(actions.typesList.create.getRequest(item.id)),
                onChange && onChange();
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On org change:", { error });
            dispatch(actions.toast.create.error(strings("errors.onOrgChange")));
        }
    };

    const [searchTerm, setSearchTerm] = useState("");

    const filteredData = useMemo(() => {
        return data.filter(
            (item) =>
                item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (item.type &&
                    item.type.name &&
                    item.type.name
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()))
        );
    }, [data, searchTerm]);
    return (
        <Dialog open={dialogOpen} maxWidth="lg" fullWidth>
            <DialogTitle>{strings("organisation")}</DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
                <input
                    type="text"
                    placeholder="Search organisations..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                        width: "100%",
                        padding: "10px 12px",
                        marginBottom: "20px",
                        border: "1px solid #e0e0e0",
                        borderRadius: "4px",
                        fontSize: "14px",
                        boxSizing: "border-box"
                    }}
                />
                <List
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(5, 1fr)",
                        gap: 3
                    }}
                >
                    {filteredData.map((item) => (
                        <ListItem
                            button
                            key={item.id}
                            onClick={handleClick(item)}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: 2,
                                border: "1px solid #e0e0e0",
                                borderRadius: 1,
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                "&:hover": {
                                    boxShadow: 2,
                                    borderColor: "#1976d2"
                                }
                            }}
                        >
                            <ListItemAvatar sx={{ mb: 1, width: "100%" }}>
                                <Avatar
                                    src={item.logoPath}
                                    sx={{
                                        width: 80,
                                        height: 80,
                                        mx: "auto"
                                    }}
                                />
                            </ListItemAvatar>
                            <ListItemText
                                primary={
                                    <Typography align="center">
                                        {item.name
                                            .split(
                                                new RegExp(
                                                    `(${searchTerm})`,
                                                    "gi"
                                                )
                                            )
                                            .map((part, idx) =>
                                                part.toLowerCase() ===
                                                searchTerm.toLowerCase() ? (
                                                    <mark
                                                        key={idx}
                                                        style={{
                                                            backgroundColor:
                                                                "#fff59d",
                                                            fontWeight: 600
                                                        }}
                                                    >
                                                        {part}
                                                    </mark>
                                                ) : (
                                                    part
                                                )
                                            )}
                                    </Typography>
                                }
                                secondary={
                                    <Typography component="span" align="center">
                                        {item.type?.name
                                            ?.split(
                                                new RegExp(
                                                    `(${searchTerm})`,
                                                    "gi"
                                                )
                                            )
                                            .map((part, idx) =>
                                                part.toLowerCase() ===
                                                searchTerm.toLowerCase() ? (
                                                    <mark
                                                        key={idx}
                                                        style={{
                                                            backgroundColor:
                                                                "#fff59d"
                                                            // fontWeight: 600
                                                        }}
                                                    >
                                                        {part}
                                                    </mark>
                                                ) : (
                                                    part
                                                )
                                            )}
                                    </Typography>
                                }
                                primaryTypographyProps={{
                                    textAlign: "center",
                                    variant: "body2",
                                    sx: { fontWeight: 600 }
                                }}
                                secondaryTypographyProps={{
                                    textAlign: "center",
                                    variant: "caption"
                                }}
                                sx={{ width: "100%" }}
                            />
                        </ListItem>
                    ))}
                </List>
            </DialogContent>
        </Dialog>
    );
};
